import React, { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { BeatLoader } from 'react-spinners'
import Sidebar from '../../layouts/Sidebar'
import Topbar from '../../layouts/Topbar'
import api from '../../config/api'

/* ─── helpers ─────────────────────────────────────────────────────────────── */

const getErrorMessage = (err, fallback) => {
    const data = err.response?.data
    if (!data) return fallback
    if (typeof data === 'string') return data
    if (data.detail) return data.detail
    if (data.message) return data.message
    return Object.entries(data)
        .map(([field, messages]) =>
            `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`
        )
        .join(' | ')
}

const addDays = (days) => {
    const date = new Date()
    date.setDate(date.getDate() + (parseInt(days, 10) || 0))
    return date.toISOString().split('T')[0]
}

const emptyLineItem = (sortOrder = 0) => ({
    product: '',
    name: '',
    description: '',
    quantity: '1.00',
    unit_price: '0.00',
    sort_order: sortOrder,
})

const emptyForm = {
    name: '',
    client: '',
    company_name: '',
    company_logo: '',
    frequency: 'monthly',
    is_active: true,
    next_run_date: '',
    currency: 'USD',
    due_days: 30,
    due_date: addDays(30),
    auto_send: false,
    payment_instructions: '',
    notes: '',
}

/* ─── component ───────────────────────────────────────────────────────────── */

const CreateInvoice = () => {
    const navigate = useNavigate()

    const [formData, setFormData] = useState(emptyForm)
    const [logoPreview, setLogoPreview] = useState(null)
    const [lineItems, setLineItems] = useState([emptyLineItem(0)])

    const [clients, setClients] = useState([])
    const [products, setProducts] = useState([])
    const [clientsLoading, setClientsLoading] = useState(true)
    const [productsLoading, setProductsLoading] = useState(true)

    const [submitting, setSubmitting] = useState(false)
    const [formError, setFormError] = useState(null)
    const [successMessage, setSuccessMessage] = useState(null)

    /* ── fetch dropdown data ── */
    const fetchClients = useCallback(async () => {
        setClientsLoading(true)
        try {
            const res = await api.get('/clients/')
            setClients(Array.isArray(res.data) ? res.data : (res.data?.results ?? []))
        } catch (err) {
            console.error('Failed to fetch clients', err)
        } finally {
            setClientsLoading(false)
        }
    }, [])

    const fetchProducts = useCallback(async () => {
        setProductsLoading(true)
        try {
            const res = await api.get('/products/')
            setProducts(Array.isArray(res.data) ? res.data : (res.data?.results ?? []))
        } catch (err) {
            console.error('Failed to fetch products', err)
        } finally {
            setProductsLoading(false)
        }
    }, [])

    useEffect(() => {
        fetchClients()
        fetchProducts()
    }, [fetchClients, fetchProducts])

    /* ── form handlers ── */
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target
        if (name === 'due_days') {
            setFormData((prev) => ({
                ...prev,
                due_days: value,
                due_date: value !== '' ? addDays(value) : '',
            }))
        } else {
            setFormData((prev) => ({
                ...prev,
                [name]: type === 'checkbox' ? checked : value,
            }))
        }
    }

    /* ── logo upload handler ── */
    const handleLogoChange = (e) => {
        const file = e.target.files?.[0]
        if (!file) return
        const reader = new FileReader()
        reader.onload = (ev) => {
            const dataUrl = ev.target.result
            setLogoPreview(dataUrl)
            setFormData((prev) => ({ ...prev, company_logo: dataUrl }))
        }
        reader.readAsDataURL(file)
    }

    /* ── line-item handlers ── */
    const handleLineItemChange = (index, e) => {
        const { name, value } = e.target
        setLineItems((prev) => {
            const updated = [...prev]
            updated[index] = { ...updated[index], [name]: value }
            return updated
        })
    }

    /**
     * When a product is selected from the per-row dropdown we pre-fill the
     * name, description and unit_price from the product catalogue.
     */
    const handleLineItemProductChange = (index, productId) => {
        const product = products.find((p) => String(p.id) === String(productId))
        setLineItems((prev) => {
            const updated = [...prev]
            updated[index] = {
                ...updated[index],
                product: productId,
                name: product?.name ?? updated[index].name,
                description: product?.description ?? updated[index].description,
                unit_price: product?.price ?? product?.unit_price ?? updated[index].unit_price,
            }
            return updated
        })
    }

    const addLineItem = () => {
        setLineItems((prev) => [...prev, emptyLineItem(prev.length)])
    }

    const removeLineItem = (index) => {
        setLineItems((prev) =>
            prev
                .filter((_, i) => i !== index)
                .map((item, i) => ({ ...item, sort_order: i }))
        )
    }

    /* ── submission ── */
    const handleSubmit = async (e) => {
        e.preventDefault()
        setSubmitting(true)
        setFormError(null)
        setSuccessMessage(null)

        const payload = {
            ...formData,
            client: Number(formData.client),
            due_days: Number(formData.due_days),
            due_date: formData.due_date,
            line_items: lineItems.map((item, i) => ({
                ...(item.product ? { product: Number(item.product) } : {}),
                name: item.name,
                description: item.description,
                quantity: item.quantity,
                unit_price: item.unit_price,
                sort_order: i,
            })),
        }

        try {
            await api.post('/invoices/', payload)
            setSuccessMessage('Invoice created successfully.')
            setTimeout(() => navigate('/invoices'), 1200)
        } catch (err) {
            console.error('Failed to create invoice', err)
            setFormError(getErrorMessage(err, 'Failed to create invoice. Please try again.'))
        } finally {
            setSubmitting(false)
        }
    }

    /* ── helpers ── */
    const getClientLabel = (client) => {
        const name = [client.first_name, client.last_name].filter(Boolean).join(' ')
        return name || client.company?.name || client.name || `Client #${client.id}`
    }

    const lineItemTotal = (item) => {
        const qty = parseFloat(item.quantity) || 0
        const price = parseFloat(item.unit_price) || 0
        return (qty * price).toFixed(2)
    }

    const grandTotal = lineItems.reduce((sum, item) => {
        return sum + (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0)
    }, 0)

    /* ── render ── */
    return (
        <div data-sidebar-skin="dark" data-header-skin="light" data-navbar-brand-skin="dark" data-sidebar-state="expand">
            <Topbar />
            <Sidebar />

            <div className="page-wrapper">
                <div className="content pb-0">

                    {/* Page header */}
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4">
                        <div>
                            <h4 className="fw-bold mb-1">Create Invoice</h4>
                        </div>
                        <Link to="/invoices" className="btn btn-light">
                            <i className="ti ti-arrow-left me-1" />
                            Back to Invoices
                        </Link>
                    </div>

                    <form onSubmit={handleSubmit}>
                        {/* Alerts */}
                        {formError && (
                            <div className="alert alert-danger mb-4" role="alert">
                                <i className="ti ti-alert-circle me-2" />
                                {formError}
                            </div>
                        )}
                        {successMessage && (
                            <div className="alert alert-success mb-4" role="alert">
                                <i className="ti ti-check me-2" />
                                {successMessage}
                            </div>
                        )}

                        {/* ── Invoice Details ── */}
                        <div className="card shadow-sm mb-4">
                            <div className="card-header">
                                <h5 className="fw-bold mb-0">Invoice Details</h5>
                            </div>
                            <div className="card-body">
                                <div className="row g-3">

                                    {/* Invoice Name */}
                                    <div className="col-md-6">
                                        <label htmlFor="inv-name" className="form-label">
                                            Invoice Name <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            id="inv-name"
                                            type="text"
                                            name="name"
                                            className="form-control"
                                            placeholder="e.g. Acme Monthly Retainer"
                                            value={formData.name}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>

                                    {/* Company Name */}
                                    <div className="col-md-6">
                                        <label htmlFor="inv-company-name" className="form-label">
                                            Company Name <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            id="inv-company-name"
                                            type="text"
                                            name="company_name"
                                            className="form-control"
                                            placeholder="e.g. Acme Corporation"
                                            value={formData.company_name}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>

                                    {/* Company Logo */}
                                    <div className="col-md-12">
                                        <label htmlFor="inv-company-logo" className="form-label">
                                            Company Logo
                                        </label>
                                        <div className="d-flex align-items-center gap-3 flex-wrap">
                                            {logoPreview && (
                                                <img
                                                    src={logoPreview}
                                                    alt="Company logo preview"
                                                    style={{
                                                        height: 64,
                                                        maxWidth: 180,
                                                        objectFit: 'contain',
                                                        border: '1px solid #dee2e6',
                                                        borderRadius: 6,
                                                        padding: 4,
                                                        background: '#fff',
                                                    }}
                                                />
                                            )}
                                            <div className="flex-fill">
                                                <input
                                                    id="inv-company-logo"
                                                    type="file"
                                                    accept="image/*"
                                                    className="form-control"
                                                    onChange={handleLogoChange}
                                                />
                                                <div className="form-text">
                                                    Accepted formats: PNG, JPG, SVG, WEBP. Will appear on the invoice.
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Client */}
                                    <div className="col-md-6">
                                        <label htmlFor="inv-client" className="form-label">
                                            Client <span className="text-danger">*</span>
                                        </label>
                                        <select
                                            id="inv-client"
                                            name="client"
                                            className="form-select"
                                            value={formData.client}
                                            onChange={handleChange}
                                            required
                                            disabled={clientsLoading}
                                        >
                                            <option value="">
                                                {clientsLoading ? 'Loading clients…' : '— Select a client —'}
                                            </option>
                                            {clients.map((c) => (
                                                <option key={c.id} value={c.id}>
                                                    {getClientLabel(c)}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Currency */}
                                    <div className="col-md-4">
                                        <label htmlFor="inv-currency" className="form-label">
                                            Currency <span className="text-danger">*</span>
                                        </label>
                                        <select
                                            id="inv-currency"
                                            name="currency"
                                            className="form-select"
                                            value={formData.currency}
                                            onChange={handleChange}
                                            required
                                        >
                                            <option value="USD">USD — US Dollar</option>
                                            <option value="ZIG">ZIG — Zimbabwe Gold</option>
                                        </select>
                                    </div>

                                    {/* Frequency */}
                                    <div className="col-md-4">
                                        <label htmlFor="inv-frequency" className="form-label">
                                            Frequency <span className="text-danger">*</span>
                                        </label>
                                        <select
                                            id="inv-frequency"
                                            name="frequency"
                                            className="form-select"
                                            value={formData.frequency}
                                            onChange={handleChange}
                                            required
                                        >
                                            <option value="monthly">Monthly</option>
                                            <option value="quarterly">Quarterly</option>
                                            <option value="annually">Annually</option>
                                        </select>
                                    </div>

                                    {/* Due Days */}
                                    <div className="col-md-4">
                                        <label htmlFor="inv-due-days" className="form-label">
                                            Due Days <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            id="inv-due-days"
                                            type="number"
                                            name="due_days"
                                            className="form-control"
                                            min="0"
                                            placeholder="30"
                                            value={formData.due_days}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>

                                    {/* Due Date (auto-calculated from due days) */}
                                    <div className="col-md-4">
                                        <label htmlFor="inv-due-date" className="form-label">
                                            Due Date <span className="text-danger">*</span>
                                            {formData.due_days !== '' && (
                                                <span className="text-muted fs-12 ms-1">(auto-calculated)</span>
                                            )}
                                        </label>
                                        <input
                                            id="inv-due-date"
                                            type="date"
                                            name="due_date"
                                            className="form-control"
                                            value={formData.due_date}
                                            onChange={handleChange}
                                            required
                                        />
                                        {formData.due_days !== '' && (
                                            <div className="form-text">
                                                Today&nbsp;+&nbsp;{formData.due_days}&nbsp;day{Number(formData.due_days) !== 1 ? 's' : ''}
                                            </div>
                                        )}
                                    </div>

                                    {/* Next Run Date */}
                                    <div className="col-md-4">
                                        <label htmlFor="inv-next-run" className="form-label">
                                            Next Run Date <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            id="inv-next-run"
                                            type="date"
                                            name="next_run_date"
                                            className="form-control"
                                            value={formData.next_run_date}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>

                                    {/* Is Active */}
                                    <div className="col-md-4 d-flex align-items-center gap-3 pt-4">
                                        <div className="form-check form-switch mb-0">
                                            <input
                                                id="inv-is-active"
                                                className="form-check-input"
                                                type="checkbox"
                                                name="is_active"
                                                checked={formData.is_active}
                                                onChange={handleChange}
                                            />
                                            <label className="form-check-label fw-medium" htmlFor="inv-is-active">
                                                Active
                                            </label>
                                        </div>
                                        <div className="form-check form-switch mb-0">
                                            <input
                                                id="inv-auto-send"
                                                className="form-check-input"
                                                type="checkbox"
                                                name="auto_send"
                                                checked={formData.auto_send}
                                                onChange={handleChange}
                                            />
                                            <label className="form-check-label fw-medium" htmlFor="inv-auto-send">
                                                Auto Send
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* ── Line Items ── */}
                        <div className="card shadow-sm mb-4">
                            <div className="card-header d-flex align-items-center justify-content-between">
                                <h5 className="fw-bold mb-0">Products / Services</h5>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-primary"
                                    onClick={addLineItem}
                                    disabled={productsLoading}
                                >
                                    <i className="ti ti-plus me-1" />
                                    Add Line Item
                                </button>
                            </div>
                            <div className="card-body p-0">
                                <div className="table-responsive">
                                    <table className="table table-nowrap border mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th style={{ minWidth: 180 }}>Product / Service</th>
                                                <th style={{ minWidth: 160 }}>Name</th>
                                                <th style={{ minWidth: 200 }}>Description</th>
                                                <th style={{ minWidth: 100 }}>Qty</th>
                                                <th style={{ minWidth: 120 }}>Unit Price</th>
                                                <th style={{ minWidth: 110 }}>Total</th>
                                                <th style={{ width: 50 }} />
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {lineItems.map((item, index) => (
                                                <tr key={index}>
                                                    {/* Product select */}
                                                    <td>
                                                        <select
                                                            className="form-select form-select-sm"
                                                            value={item.product}
                                                            onChange={(e) =>
                                                                handleLineItemProductChange(index, e.target.value)
                                                            }
                                                            disabled={productsLoading}
                                                            aria-label={`Product for line item ${index + 1}`}
                                                        >
                                                            <option value="">
                                                                {productsLoading ? 'Loading…' : '— Select —'}
                                                            </option>
                                                            {products.map((p) => (
                                                                <option key={p.id} value={p.id}>
                                                                    {p.name}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </td>

                                                    {/* Name */}
                                                    <td>
                                                        <input
                                                            type="text"
                                                            name="name"
                                                            className="form-control form-control-sm"
                                                            placeholder="Item name"
                                                            value={item.name}
                                                            onChange={(e) => handleLineItemChange(index, e)}
                                                            required
                                                            aria-label={`Name for line item ${index + 1}`}
                                                        />
                                                    </td>

                                                    {/* Description */}
                                                    <td>
                                                        <input
                                                            type="text"
                                                            name="description"
                                                            className="form-control form-control-sm"
                                                            placeholder="Short description"
                                                            value={item.description}
                                                            onChange={(e) => handleLineItemChange(index, e)}
                                                            aria-label={`Description for line item ${index + 1}`}
                                                        />
                                                    </td>

                                                    {/* Quantity */}
                                                    <td>
                                                        <input
                                                            type="number"
                                                            name="quantity"
                                                            className="form-control form-control-sm"
                                                            min="0.01"
                                                            step="0.01"
                                                            value={item.quantity}
                                                            onChange={(e) => handleLineItemChange(index, e)}
                                                            required
                                                            aria-label={`Quantity for line item ${index + 1}`}
                                                        />
                                                    </td>

                                                    {/* Unit Price */}
                                                    <td>
                                                        <input
                                                            type="number"
                                                            name="unit_price"
                                                            className="form-control form-control-sm"
                                                            min="0"
                                                            step="0.01"
                                                            value={item.unit_price}
                                                            onChange={(e) => handleLineItemChange(index, e)}
                                                            required
                                                            aria-label={`Unit price for line item ${index + 1}`}
                                                        />
                                                    </td>

                                                    {/* Row total */}
                                                    <td className="fw-medium text-end">
                                                        {formData.currency === 'USD' ? '$' : 'ZIG '}
                                                        {lineItemTotal(item)}
                                                    </td>

                                                    {/* Remove */}
                                                    <td className="text-center">
                                                        {lineItems.length > 1 && (
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-outline-danger p-1"
                                                                onClick={() => removeLineItem(index)}
                                                                title="Remove line item"
                                                                aria-label={`Remove line item ${index + 1}`}
                                                            >
                                                                <i className="ti ti-trash" />
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot className="table-light">
                                            <tr>
                                                <td colSpan={5} className="text-end fw-bold">Grand Total</td>
                                                <td className="fw-bold text-end">
                                                    {formData.currency === 'USD' ? '$' : 'ZIG '}
                                                    {grandTotal.toFixed(2)}
                                                </td>
                                                <td />
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                            </div>
                        </div>

                        {/* ── Additional Info ── */}
                        <div className="card shadow-sm mb-4">
                            <div className="card-header">
                                <h5 className="fw-bold mb-0">Additional Information</h5>
                            </div>
                            <div className="card-body">
                                <div className="row g-3">

                                    {/* Payment Instructions */}
                                    <div className="col-md-6">
                                        <label htmlFor="inv-payment-instructions" className="form-label">
                                            Payment Instructions
                                        </label>
                                        <textarea
                                            id="inv-payment-instructions"
                                            name="payment_instructions"
                                            className="form-control"
                                            rows={4}
                                            placeholder="e.g. Pay via bank transfer to Account #123456"
                                            value={formData.payment_instructions}
                                            onChange={handleChange}
                                        />
                                    </div>

                                    {/* Notes */}
                                    <div className="col-md-6">
                                        <label htmlFor="inv-notes" className="form-label">
                                            Notes
                                        </label>
                                        <textarea
                                            id="inv-notes"
                                            name="notes"
                                            className="form-control"
                                            rows={4}
                                            placeholder="e.g. Recurring monthly maintenance fee"
                                            value={formData.notes}
                                            onChange={handleChange}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* ── Actions ── */}
                        <div className="d-flex align-items-center justify-content-end gap-2 mb-4">
                            <Link
                                to="/invoices"
                                className="btn btn-light"
                                onClick={(e) => submitting && e.preventDefault()}
                            >
                                Cancel
                            </Link>
                            <button type="submit" className="btn btn-primary" disabled={submitting}>
                                {submitting ? <BeatLoader color="#fff" size={8} /> : 'Create Invoice'}
                            </button>
                        </div>
                    </form>
                </div>

                <div className="footer d-flex align-items-center justify-content-between flex-column flex-sm-row row-gap-2 border-top py-2 px-3">
                    <p className="text-dark mb-0">
                        {new Date().getFullYear()} © <span className="link-primary">Burray Invoice</span>, All Rights Reserved
                    </p>
                </div>
            </div>
        </div>
    )
}

export default CreateInvoice